import React, { useEffect, useRef, useState } from 'react';

/**
 * Props:
 *  - isOpen: boolean => whether to show the bottom sheet
 *  - type: 'warning' | 'tempBan' | 'ban'
 *  - reason: string => the reason for the moderation action
 *  - banExpiresAt?: Date | string => relevant if type === 'tempBan'
 *  - reportNumber?: string => "Report #xxxxx"
 *  - onClose: function => called when user clicks the close/OK button
 */

function isPWA() {
  return (
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true
  );
}

export default function ModerationBottomSheet({
  isOpen,
  type,
  reason,
  banExpiresAt,
  reportNumber,
  onClose,
}) {
  // ─────────────────────────────────────────────────────────────
  // 1) Prevent Scrolling Behind the Sheet
  // ─────────────────────────────────────────────────────────────
  const scrollPositionRef = useRef(0);

  useEffect(() => {
    const themeMetaTag = document.querySelector('meta[name="theme-color"]');
  
    if (isOpen) {
      scrollPositionRef.current = window.scrollY || window.pageYOffset;
      document.body.style.top = `-${scrollPositionRef.current}px`;
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.classList.add('overlay-active');
      document.documentElement.classList.add('overlay-active');
      document.getElementById('root').classList.add('overlay-active');
      if (themeMetaTag) themeMetaTag.content = '#999998';
    } else {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollPositionRef.current);
      document.body.classList.remove('overlay-active');
      document.documentElement.classList.remove('overlay-active');
      document.getElementById('root').classList.remove('overlay-active');
      if (themeMetaTag) themeMetaTag.content = '#ffffff';
    }
  
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollPositionRef.current);
      document.body.classList.remove('overlay-active');
      document.documentElement.classList.remove('overlay-active');
      document.getElementById('root').classList.remove('overlay-active');
      if (themeMetaTag) themeMetaTag.content = '#ffffff';
    };
  }, [isOpen]);
  

  
  // ─────────────────────────────────────────────────────────────
  // 2) Live Countdown (Only for tempBan)
  // ─────────────────────────────────────────────────────────────
  const [countdown, setCountdown] = useState('');

  useEffect(() => {
    if (!isOpen || type !== 'tempBan' || !banExpiresAt) {
      setCountdown('');
      return;
    }

    function updateCountdown() {
      const now = new Date();
      const expires = new Date(banExpiresAt);
      const ms = expires - now;

      if (ms <= 0) {
        setCountdown('00:00:00');
        return;
      }
      // Convert ms to D:HH:MM
      const totalSeconds = Math.floor(ms / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);

      const dd = String(days).padStart(2, '0');
      const hh = String(hours).padStart(2, '0');
      const mm = String(minutes).padStart(2, '0');
      setCountdown(`${dd}:${hh}:${mm}`);
    }

    updateCountdown();
    const timerId = setInterval(updateCountdown, 1000);
    return () => clearInterval(timerId);
  }, [isOpen, type, banExpiresAt]);

  // ─────────────────────────────────────────────────────────────
  // 3) Return NULL If Not Open
  // ─────────────────────────────────────────────────────────────
  if (!isOpen) return null;

  // ─────────────────────────────────────────────────────────────
  // 4) Determine Final Texts
  // ─────────────────────────────────────────────────────────────
  const ctaLabel = 'I Understand';

  // If 'reason' is empty, we fall back on a default message
  const finalMessage = reason?.trim()
    ? reason
    : type === 'tempBan'
    ? 'Your access is restricted for a temporary period.'
    : type === 'warning'
    ? 'You have received a warning.'
    : 'You no longer have access to this service.';

  // Helper to handle "resources" click for the warning screen
  async function handleResourcesClick() {
    try {
      // 1) Clear the warning from DB so it doesn't keep coming up
      //    (Replace this with your actual API call or logic)
      await clearWarningFromDb();

      // 2) Close the bottom sheet
      onClose();

      // 3) Navigate to /LegalPage
      window.location.href = '/legal/community-guidelines';
    } catch (err) {
      console.error('Error clearing warning:', err);
    }
  }

  // Dummy function: replace with your actual DB update call
  async function clearWarningFromDb() {
    // Example fetch/axios call:
    // await fetch('/api/clear-warning', { method: 'POST' });
  }

  // ─────────────────────────────────────────────────────────────
  // 5) Render the Bottom Sheet
  // ─────────────────────────────────────────────────────────────
  return (
    <div style={backdropStyle}>
      <div style={sheetContainerStyle}>
        {/* Header: Only shows the close (×) button */}
        <div style={sheetHeaderStyle}>
          <button onClick={onClose} style={headerCloseBtnStyle}>
            ×
          </button>
        </div>

        {/* Main Body */}
        <div style={{ padding: '24px', textAlign: 'center' }}>
          {/* WARNING */}
          {type === 'warning' && (
            <>
              <div style={{ marginBottom: '1.5rem' }}>
                {/* Use the same "slash circle" icon as the ban */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="60"
                  height="60"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#000"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="5" y1="5" x2="19" y2="19" />
                </svg>
              </div>

              {/* Headline: You are being warned for */}
              <h2 style={{ fontSize: '1.4rem', marginBottom: '0rem', fontWeight: 400 }}>
                You are being warned for
              </h2>

              {/* Reason on its own line */}
              <p
                style={{
                  marginBottom: '1rem',
                  fontWeight: 700,
                  fontSize: '1.4rem',
                  color: '#444',
                  marginTop: 0,
                }}
              >
                {finalMessage}
              </p>

              {/* Ticket #xxx => smaller font */}
              {reportNumber && (
                <p style={{ marginBottom: '1rem', color: '#444', fontSize: '1rem' }}>
                  #{reportNumber}
                </p>
              )}

              {/* Resources line: clickable, underlined, blue */}
              <p style={{ marginBottom: '1.5rem', marginTop: 0, color: '#444' }}>
                Review our{' '}
                <span
                  onClick={handleResourcesClick}
                  style={{
                    textDecoration: 'underline',
                    color: '#2563EB',
                    cursor: 'pointer',
                  }}
                >
                  resources
                </span>{' '}
                to
                <br />
                learn more about our expectations.
              </p>
            </>
          )}

          {/* TEMP BAN */}
          {type === 'tempBan' && (
            <>
              {/* (A) Countdown UI */}
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                  {countdown}
                </div>
                <div style={{ color: '#999', fontSize: '0.9rem' }}>
                  Days &nbsp;&nbsp; Hrs &nbsp;&nbsp; Mins
                </div>
              </div>

              {/* (B) Headline */}
              <h2
                style={{
                  fontSize: '1.4rem',
                  marginBottom: '0rem',
                  fontWeight: 400,
                }}
              >
                You are temporarily banned for
              </h2>

              {/* Reason on its own line */}
              <p
                style={{
                  marginBottom: '1.5rem',
                  fontWeight: 700,
                  fontSize: '1.4rem',
                  marginTop: 0,
                  color: '#444',
                }}
              >
                {finalMessage}
              </p>

              {/* Ticket #xxx => smaller font */}
              {reportNumber && (
                <p style={{ marginBottom: '1.5rem', color: '#444', fontSize: '1rem' }}>
                  #{reportNumber}
                </p>
              )}

              <p style={{ marginBottom: '2rem', color: '#444' }}>
                If you would like to argue this ruling, please reach out at
                <br />
                <strong>moderation@jointribe.io</strong>
              </p>
            </>
          )}

          {/* PERMANENT BAN */}
          {type === 'ban' && (
            <>
              <div style={{ marginBottom: '1.5rem' }}>
                {/* Slash circle icon */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="60"
                  height="60"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#000"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="5" y1="5" x2="19" y2="19" />
                </svg>
              </div>

              {/* Headline */}
              <h2
                style={{
                  fontSize: '1.4rem',
                  fontWeight: 400,
                  marginBottom: '.25rem',
                }}
              >
                You are banned for
              </h2>

              {/* Reason on its own line */}
              <p
                style={{
                  marginBottom: '1.5rem',
                  marginTop: 0,
                  fontWeight: 700,
                  fontSize: '1.4rem',
                  color: '#444',
                }}
              >
                {finalMessage}
              </p>

              {/* Ticket #xxx => smaller font */}
              {reportNumber && (
                <p style={{ marginBottom: '1.5rem', color: '#444', fontSize: '1rem' }}>
                  #{reportNumber}
                </p>
              )}

              <p style={{ marginBottom: '2rem', color: '#444' }}>
                If you would like to argue this ruling, please reach out at
                <br />
                <strong>moderation@jointribe.io</strong>
              </p>
            </>
          )}
        </div>

        {/* Footer Button */}
        <div style={footerContainerStyle}>
          <button onClick={onClose} style={footerBtnStyle}>
            {ctaLabel}
          </button>
        </div>
      </div>

      {/* Slide-Up Animation */}
      <style>
        {`
          @keyframes slideUp {
            from { transform: translateY(100%); opacity: 0; }
            to   { transform: translateY(0); opacity: 1; }
          }
        `}
      </style>
    </div>
  );
}

/* ===============================
         STYLES
=============================== */

const backdropStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 99999,
  backgroundColor: 'rgba(0, 0, 0, 0.4)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-end',
  pointerEvents: 'auto',
};

const sheetContainerStyle = {
  width: '100%',
  maxWidth: 600,
  backgroundColor: '#fff',
  borderRadius: 12,
  margin: `0 8px ${isPWA() ? 40 : 8}px 8px`, // Adjust bottom margin conditionally for PWA
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  fontFamily: "'Open Sans', sans-serif",
  overflow: 'hidden',
  animation: 'slideUp 0.3s ease-out',
};

const sheetHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end', // only the close button
  borderBottom: '1px solid #eee',
  padding: '8px 16px',
};

const headerCloseBtnStyle = {
  border: 'none',
  background: 'none',
  fontSize: '1.5rem',
  cursor: 'pointer',
  lineHeight: 1,
};

const footerContainerStyle = {
  borderTop: '1px solid #eee',
  padding: '16px',
  display: 'flex',
  justifyContent: 'flex-end',
};

const footerBtnStyle = {
  border: 'none',
  borderRadius: 8,
  fontSize: 15,
  fontWeight: 600,
  padding: '12px 20px',
  cursor: 'pointer',
  fontFamily: "'Open Sans', sans-serif",
  background: 'linear-gradient(90deg, #3B82F6, #2563EB)',
  color: '#fff',
};
